import {
  AfterViewInit,
  Component,
  computed,
  EventEmitter,
  inject,
  Input,
  linkedSignal,
  OnDestroy,
  OnInit,
  Output,
  Signal,
  signal,
  ViewChild,
} from '@angular/core';
import {
  AddressDTO,
  API,
  AttributeMapDTO,
  AttributeType,
  CompanyFullDTO,
  CullDTO,
  Order,
  PlayerCullDTO,
  PoolDTO,
  ReportingFullDTO,
  shared_Company_export_filter,
  shared_Company_export_order,
  shared_Company_retrieveAll_filter,
  shared_Company_retrieveAll_order,
} from '../../../services/api/api';
import { ErrorService } from '../../../services/error.service';
import { forkJoin, from, of, switchMap } from 'rxjs';
import { UtilService } from '../../../services/util.service';
import { ModalNavigationService } from '../../../services/gui/modal/modal-navigation.service';
import { TranslateService } from '@ngx-translate/core';
import { TagService } from '../../../services/tag.service';
import {
  IDAndTypeInterface,
  OrderOption,
  SbCompaniesListColumn,
  SbSplitButton,
  SelectionState,
} from '../../../services/global/global-interfaces';
import { PermissionService } from '../../../services/permission.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { finalize, take } from 'rxjs/operators';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ReportingModalComponent } from '../../../components/standard-reporting/reporting-modal/reporting-modal.component';
import { ReportingService } from '../../../services/reporting.service';
import { createAttributeColumns, getPropsFromDTO, isColumnMatch } from 'src/app/services/global/global-methods';
import { clientColumnDefaultsCompanies } from './clientColumnDefaultsCompanies';
import { DynamicTableBase } from '../../../components/dynamic-table-elements/DynamicTableBase';
import { FilterPanelComponent } from '@ngx-seatback/layout';
import { SbSplitButtonItem, SearchOptions } from '@ngx-seatback/controls';
import { CompanyFullDTOAndTags } from '../../../models/taggable.model';
import { MultiSelectButtonAction } from '../../../components/table-multi-select-button/table-multi-select-button.component';
import { RoutingID } from '../../../routes/sb-routing-enum';

type ToggleContext = 'hasCampaigns' | 'hasMedia' | 'hasMediumGroups' | 'hasCulls' | 'managesLocations' | 'isCustomer' | undefined;
type SplitButtonAction = 'delete';
type CompanyFullDTOAndTagsAndButtons = CompanyFullDTOAndTags & SbSplitButton<SplitButtonAction>;

@Component({
  selector: 'sb-page-companies',
  templateUrl: './companies.component.html',
  styleUrls: ['./companies.component.scss'],
  standalone: false,
})
export class CompaniesComponent extends DynamicTableBase<SbCompaniesListColumn> implements OnInit, AfterViewInit, OnDestroy {
  // injects
  public readonly api = inject(API);
  public readonly util = inject(UtilService);
  private readonly errorHandler = inject(ErrorService);
  private readonly modalNavigation = inject(ModalNavigationService);
  public readonly translate = inject(TranslateService);
  protected readonly tagService = inject(TagService);
  protected readonly permissionService = inject(PermissionService);
  protected readonly reportingService = inject(ReportingService);
  // global reference imports
  protected window = window;
  @ViewChild('FilterPanelRef') filterPanelRef: FilterPanelComponent;

  @Input() isInModalContext: boolean = false;
  @Input() isInIntegratedContext: boolean = false;
  @Input() toggleContext: ToggleContext = undefined;

  @Output() selectedCompany = new EventEmitter<CompanyFullDTO>();

  /* filter Variables */
  // search
  search: string | null = null;
  exactMatch: SearchOptions = 'contains';
  /* Threeway-toggles */
  hasCampaigns: boolean | null = null;
  hasMedia: boolean | null = null;
  hasMediumGroups: boolean | null = null;
  hasCulls: boolean | null = null;
  managesLocations: boolean | null = null;
  isCustomer: boolean | null = null;
  // ID filtering
  cull: CullDTO | null = null;

  /* ordering */
  orderOption = signal<OrderOption<SbCompaniesListColumn>>({
    direction: Order.asc,
    value: {
      id: 'name',
      title: 'lang.globals.name',
      selected: false,

      type: 'PROPERTY',
      orderId: shared_Company_retrieveAll_order.NAME,
      canOrder: true,
      filterId: shared_Company_retrieveAll_filter.NAME,
      canFilter: true,
    },
  });
  filterPanelOrderOption = linkedSignal<OrderOption<SbCompaniesListColumn>, OrderOption<SbCompaniesListColumn>>({
    source: this.orderOption,
    computation: orderOption => {
      return { ...orderOption };
    },
  });
  allOrderOptions: OrderOption<SbCompaniesListColumn>[] = [];

  // export
  exportLoading: boolean = false;
  isSelectedCompaniesExport: boolean = false;
  form: FormGroup = new FormGroup({
    Title: new FormControl('', [Validators.required]),
  });

  /* global */
  companies: CompanyFullDTOAndTagsAndButtons[] = [];
  showSideBar: boolean = false;
  selectedCompanies = signal<Map<number, CompanyFullDTOAndTagsAndButtons>>(new Map());
  selectionState: Signal<SelectionState> = computed(() => {
    const currentlySelectedCompanies = this.selectedCompanies();
    if (!this.companies || this.companies.length === 0) {
      return 'none';
    }
    const allSelectedOnCurrentPage = this.companies.every(item => currentlySelectedCompanies.has(item.id));
    if (allSelectedOnCurrentPage) {
      return 'all';
    }
    const someSelectedOnCurrentPage = this.companies.some(item => currentlySelectedCompanies.has(item.id));
    if (someSelectedOnCurrentPage) {
      return 'partly';
    }
    return 'none';
  });

  constructor() {
    super();
    // has to be called before resetOrderOption to initialize columns
    this.columnDefaults = clientColumnDefaultsCompanies[this.util.getSeatbackCMSClient()] ?? clientColumnDefaultsCompanies['generic'] ?? [];
    this.resetOrderOption();
  }

  ngOnInit() {
    if (this.isInModalContext) {
      const context = this.modalNavigation.getCurrentNavigation().data.toggleContext;
      if (typeof context === 'string') {
        switch (context) {
          case 'hasCampaigns':
          case 'hasMedia':
          case 'hasMediumGroups':
          case 'hasCulls':
          case 'managesLocations':
          case 'isCustomer':
            this.toggleContext = context;
            break;
          default:
            this.toggleContext = undefined;
            break;
        }
      }
    }

    const attributeTypes: AttributeType[] = [];
    if (this.permissionService.hasPermission(this.permissionService.Permission.company_attribute_datatype_list)) {
      attributeTypes.push(AttributeType.Company);
    }

    of(attributeTypes)
      .pipe(
        switchMap((types: AttributeType[]) =>
          forkJoin({
            attributeMap: this.api.shared_Attribute_retrieve({ type: types }),
            preselectedColumns: from(this.loadSelectedColumns('companyListSelectedColumnsV1')),
          })
        ),
        take(1),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: (forkJoinResult: { attributeMap: AttributeMapDTO; preselectedColumns: IDAndTypeInterface[] | null }) => {
          this.initColumns(forkJoinResult.attributeMap, forkJoinResult.preselectedColumns);
          this.requestDebounced();
        },
        error: error => {
          this.errorHandler.errorHandler(error);
        },
      });
  }

  ngAfterViewInit() {
    // subscription for filterValue deselect
    this.initTagSubscription();
  }

  // order methods
  resetOrderOption() {
    // reset to default ordering, copy object properties to avoid changing original default
    this.orderOption.set({
      value: this.columns.find(col => col.id === 'name'),
      direction: Order.asc,
    });
  }

  setToOrderAndRefresh(pValue: SbCompaniesListColumn) {
    this.setToOrderOption(pValue);
    this.requestDebounced();
  }

  private setToOrderOption(pValue: SbCompaniesListColumn) {
    if (!pValue) {
      // nothing is set fallback to default
      this.resetOrderOption();
      return;
    }
    const currentOrderOption = this.orderOption();
    if (currentOrderOption.value === pValue) {
      if (currentOrderOption.direction === Order.asc) {
        this.orderOption.set({ ...currentOrderOption, direction: Order.desc });
      } else {
        this.orderOption.set({ ...currentOrderOption, direction: Order.asc });
      }
    } else {
      this.orderOption.set({ value: pValue, direction: Order.asc });
    }
  }

  getOrderOptions(): { by: shared_Company_retrieveAll_order; param?: string; direction: Order } {
    // if no orderOption -> return default order
    const currentOrderOption = this.orderOption();
    if (!currentOrderOption.value?.type) {
      this.resetOrderOption();
      return {
        by: shared_Company_retrieveAll_order.NAME,
        direction: Order.asc,
      };
    }

    // if value equals column title
    switch (currentOrderOption.value.type) {
      case 'COMPANY_ATTRIBUTE':
        return {
          by: shared_Company_retrieveAll_order.ATTRIBUTES,
          param: currentOrderOption.value.id,
          direction: currentOrderOption.direction,
        };
      case 'PROPERTY':
        return {
          by: currentOrderOption.value.orderId as shared_Company_retrieveAll_order,
          direction: currentOrderOption.direction,
        };
      default:
        this.resetOrderOption();
        return {
          by: shared_Company_retrieveAll_order.NAME,
          direction: Order.asc,
        };
    }
  }

  getExportOrderOptions(): { by: shared_Company_export_order; param?: string; direction: Order } {
    // if no orderOption -> return default order
    const currentOrderOption = this.orderOption();
    if (!currentOrderOption.value?.type) {
      this.resetOrderOption();
      return {
        by: shared_Company_export_order.NAME,
        direction: Order.asc,
      };
    }

    // if value equals column title
    switch (currentOrderOption.value.type) {
      case 'COMPANY_ATTRIBUTE':
        return {
          by: shared_Company_export_order.ATTRIBUTES,
          param: currentOrderOption.value.id,
          direction: currentOrderOption.direction,
        };
      case 'PROPERTY':
        return {
          by: currentOrderOption.value.orderId as shared_Company_export_order,
          direction: currentOrderOption.direction,
        };
      default:
        this.resetOrderOption();
        return {
          by: shared_Company_export_order.NAME,
          direction: Order.asc,
        };
    }
  }

  isFilterClassActive(column: SbCompaniesListColumn): boolean {
    if (column.type === 'PROPERTY') {
      const id = column.id;
      const canFilter = column?.canFilter;

      const propertyFilters = {
        // pool: this.filteredPools?.length > 0,
        // location: this.filteredLocations?.length > 0,
        // channel: this.filteredChannels?.length > 0,
        default: canFilter && this.columnFiltersMap?.has(id),
      };

      return propertyFilters[id] ?? propertyFilters.default;
    }

    const filterValue = this.columnFiltersMap?.get(column.id);

    return this.stringTransformation.stringNotNull(filterValue?.value);
  }

  // request
  refreshFromServer() {
    if (!this.pagination) {
      this.loading.set(false);
      return;
    }
    const sub = this.api
      .shared_Company_retrieveAll(
        {
          search: this.stringTransformation.encodeSearchQueryParamWithOptions(this.exactMatch, this.search),
          order: this.getOrderOptions(),
          hasCampaigns: this.hasCampaigns,
          hasMedia: this.hasMedia,
          hasMediumGroups: this.hasMediumGroups,
          hasCulls: this.hasCulls,
          managesLocations: this.managesLocations,
          isCustomer: this.isCustomer,
          playerCull: this.cull?.id ? [this.cull.id] : null,
          filter: this.getFilterOptions(),
        },
        this.pagination
      )
      .pipe(
        switchMap(comps => this.tagService.getAndAddTagEntitiesObs(this.tagService.TaggableDTO.Company, comps)),
        take(1),
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.loading.set(false);
        })
      )
      .subscribe({
        next: (companies: CompanyFullDTO[]) => {
          this.companies = this.buildSplitButtons(companies);
        },
        error: error => {
          this.errorHandler.errorHandler(error);
        },
      });
    this.subs.push(sub);
  }

  getFilterOptions(): { by: shared_Company_retrieveAll_filter; param?: string; values: string[] }[] | null {
    const ret: { by: shared_Company_retrieveAll_filter; param?: string; values: string[] }[] = [];

    if (!this.isFilterEnabled()) {
      return null;
    }

    for (const [columnId, columnFilterValue] of this.columnFiltersMap.entries()) {
      switch (columnFilterValue?.column?.type) {
        case 'COMPANY_ATTRIBUTE':
          ret.push({
            by: shared_Company_retrieveAll_filter.ATTRIBUTES,
            param: columnId,
            values: ['%' + columnFilterValue.value + '%'],
          });

          break;
        case 'PROPERTY':
          if (columnFilterValue?.column?.filterId) {
            ret.push({
              by: columnFilterValue?.column?.filterId as shared_Company_retrieveAll_filter,
              values: ['%' + columnFilterValue.value + '%'],
            });
          }
          break;
      }
    }
    return ret.length > 0 ? ret : null;
  }

  // export
  export() {
    this.loading.set(true);
    this.exportLoading = true;
    this.showSideBar = false;
    const reportName = this.form.get('Title').value;
    this.api
      .shared_Company_export({
        search: this.stringTransformation.encodeSearchQueryParamWithOptions(this.exactMatch, this.search),
        order: this.getExportOrderOptions(),
        hasCampaigns: this.hasCampaigns,
        hasMedia: this.hasMedia,
        hasMediumGroups: this.hasMediumGroups,
        hasCulls: this.hasCulls,
        managesLocations: this.managesLocations,
        isCustomer: this.isCustomer,
        playerCull: this.cull?.id ? [this.cull.id] : null,
        filter: this.getExportFilterOptions(),
        reportName: reportName ?? null,
      })
      .pipe(
        take(1),
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.loading.set(false);
        })
      )
      .subscribe({
        next: (rep: ReportingFullDTO) => {
          this.reportingService.addReport(rep);
          this.modalService
            .showNavigationModal({
              entryComponent: {
                value: ReportingModalComponent,
                data: rep,
              },
              title: this.translate.instant('lang.globals.download'),
              modalOptions: { windowClass: 'M' },
            })
            .then(() => {
              this.clearExportModal();
            })
            .catch(() => {
              this.clearExportModal();
            });
        },
        error: error => {
          this.errorHandler.errorHandler(error);
        },
      });
  }

  clearExportModal() {
    this.loading.set(false);
    this.exportLoading = false;
  }

  getExportFilterOptions(): { by: shared_Company_export_filter; param?: string; values: string[] }[] | null {
    const ret: { by: shared_Company_export_filter; param?: string; values: string[] }[] = [];

    if (!this.isFilterEnabled()) {
      return null;
    }

    for (const [columnId, columnFilterValue] of this.columnFiltersMap.entries()) {
      switch (columnFilterValue?.column?.type) {
        case 'COMPANY_ATTRIBUTE':
          ret.push({
            by: shared_Company_export_filter.ATTRIBUTES,
            param: columnId,
            values: ['%' + columnFilterValue.value + '%'],
          });

          break;
        case 'PROPERTY':
          if (columnFilterValue?.column?.filterId) {
            ret.push({
              by: columnFilterValue?.column?.filterId as shared_Company_export_filter,
              values: ['%' + columnFilterValue.value + '%'],
            });
          }
          break;
      }
    }
    return ret.length > 0 ? ret : null;
  }

  // Button methods
  buildSplitButtons<T extends { id: any }>(itemsToAdd: T[]): (T & SbSplitButton<SplitButtonAction>)[] {
    const result = itemsToAdd.map(item => {
      return { ...item, sbButtons: [] as SbSplitButtonItem<SplitButtonAction>[] };
    });
    result.forEach(item => {
      if (this.permissionService.hasPermissions([this.permissionService.Permission.company_show])) {
        item.sbButtons.push({
          icon: 'details',
          tooltip: 'lang.globals.details',
          link: RoutingID.companyDetails,
          pathParams: [{ id: 'companyID', value: item?.id }],
          queryParams: { returnTo: this.util.getPageLink() },
        });
      }
      if (this.permissionService.hasPermissions([this.permissionService.Permission.company_edit])) {
        item.sbButtons.push({
          icon: 'edit',
          tooltip: 'lang.globals.edit',
          link: RoutingID.companyEdit,
          pathParams: [{ id: 'companyID', value: item?.id }],
          queryParams: { returnTo: this.util.getPageLink() },
        });
      }
      if (this.permissionService.hasPermissions([this.permissionService.Permission.campaign_level_list])) {
        item.sbButtons.push({
          icon: 'level',
          tooltip: 'lang.companies.showCompanyLevels',
          link: RoutingID.companyLevel,
          pathParams: [{ id: 'companyID', value: item?.id }],
          queryParams: { returnTo: this.util.getPageLink() },
        });
      }
      if (this.permissionService.hasPermissions([this.permissionService.Permission.location_list])) {
        item.sbButtons.push({
          icon: 'location',
          tooltip: 'lang.companies.openLocationList',
          link: RoutingID.companyLevel,
          pathParams: [{ id: 'companyID', value: item?.id }],
          queryParams: { returnTo: this.util.getPageLink() },
        });
      }
    });
    return result;
  }

  initColumns(attributeMap: AttributeMapDTO, preselectedColumns: IDAndTypeInterface[] | null) {
    this.columns = [];

    const allColumns: string[] = [
      ...getPropsFromDTO('', Object.keys(new CompanyFullDTO())),
      ...getPropsFromDTO('address', Object.keys(new AddressDTO())),
    ];

    for (const col of allColumns) {
      const theCol = this.columnDefaults.find(defCol => defCol.id === col);
      if (theCol) {
        this.columns.push({ ...theCol });
      } else if (!col.startsWith('_')) {
        this.columns.push({ id: col, title: col, selected: false, type: 'PROPERTY' });
      }
    }

    this.columns.push(...createAttributeColumns(attributeMap.companyAttributes, 'COMPANY_ATTRIBUTE'));

    if (preselectedColumns) {
      // if there are preselectedColumns by the user then Ids should be matching and those shall be selected
      preselectedColumns.forEach(preCol => {
        const matchingCol = this.columns.find(col => isColumnMatch(col, preCol));
        if (matchingCol) {
          matchingCol.selected = true;
          this.selectedColumns.push(matchingCol);
        }
      });
    } else {
      this.selectedColumns = this.columns.filter(column => column.selected);
    }

    this.initColumnFilterMap('companyFilter');

    this.applyColumns();
  }

  applyColumns() {
    this.allOrderOptions = this.refreshAllOrderOptions();
    this.saveSelectedColumns('companyListSelectedColumnsV1');
    this.refreshColumnFilters();
  }

  // selection methods
  singleSelectCompany(e: CompanyFullDTO) {
    if (this.isInModalContext) {
      this.modalNavigation.result(e);
    } else {
      this.selectedCompany.emit(e);
    }
  }

  selectCompany(company: CompanyFullDTOAndTagsAndButtons) {
    const currentlySelectedLocations = this.selectedCompanies();
    if (currentlySelectedLocations.has(company.id)) {
      currentlySelectedLocations.delete(company.id);
      this.selectedCompanies.set(new Map(currentlySelectedLocations));
    } else {
      currentlySelectedLocations.set(company.id, company);
      this.selectedCompanies.set(new Map(currentlySelectedLocations));
    }
  }

  selectAction($event: MultiSelectButtonAction) {
    if (!this.companies || this.companies.length === 0) {
      return;
    }
    const currentlySelectedLocations = this.selectedCompanies();
    switch ($event) {
      case 'selectPage':
        this.companies.forEach(item => {
          currentlySelectedLocations.set(item.id, item);
        });
        this.selectedCompanies.set(new Map(currentlySelectedLocations));
        break;
      case 'deselectAll':
        this.selectedCompanies.set(new Map());
        break;
      case 'deselectPage':
      default:
        this.companies.forEach(item => {
          currentlySelectedLocations.delete(item.id);
        });
        this.selectedCompanies.set(new Map(currentlySelectedLocations));
        break;
    }
  }

  returnSelectedCompanies() {
    this.modalNavigation.result(Array.from(this.selectedCompanies().values()));
  }

  deselectCompanies() {
    this.selectedCompanies.set(new Map());
  }

  // filter panel methods

  openFilterPanel(element: HTMLDivElement) {
    if (!element) {
      throw new Error('Missing elementId for filter panel opening');
    }
    if (this.filterPanelRef) {
      this.filterPanelRef.isPanelExtended.set(true);
      this.scrollToFilterPanelElement(element, false);
    }
  }

  applyFiltering(filterMap: Map<string, any[]>) {
    this.setRequestOptions(filterMap);
    this.updateColumnFilters();
    this.requestDebounced();
  }

  setRequestOptions(filterMap: Map<string, any[]>) {
    // property filter
    this.hasCampaigns = filterMap.get('hasCampaigns')?.[0];
    this.hasMedia = filterMap.get('hasMedia')?.[0];
    this.hasMediumGroups = filterMap.get('hasMediumGroups')?.[0];
    this.hasCulls = filterMap.get('hasCulls')?.[0];
    this.managesLocations = filterMap.get('managesLocations')?.[0];
    this.isCustomer = filterMap.get('isCustomer')?.[0];
    // Id filter
    this.cull = filterMap.get('culls')?.map(item => item.id)?.[0];
    // search
    this.search = filterMap.get('search')?.[0];
    this.exactMatch = filterMap.get('exactMatch')?.[0];
  }

  private updateColumnFilters(): void {
    // order
    this.orderOption.set({ ...this.filterPanelOrderOption() });
    // columnFilter
    if (this.removableColumnFilters?.length > 0) {
      this.removableColumnFilters.forEach(filter => this.columnFiltersMap.delete(filter));
    }
    if (this.isInModalContext) {
      return;
    }
    this.saveColumnFilterToQueryParam('companyFilter');
  }
}
